import { useState, useEffect, useRef } from "react";
import { useLocalStorageSync } from "./useLocalStorageSync";
import { DEFAULT_TASK_DURATION } from "../config";

export const LAST_SAVED_SNAPSHOT_KEY = "lastSavedDataSnapshot";

export function useSettings() {
  const { getItem, setItem } = useLocalStorageSync(["settings"]);
  const [settings, setSettings] = useState({
    useTags: false,
    useNotes: true, // CHANGED: Default to true
    useTaskPriority: false,
    saveOnBrowserClose: true,
    addTaskOnEnter: true,
    autoCompleteOnTimer: true,
    defaultTaskDuration: DEFAULT_TASK_DURATION || 1800,
    selectedFilterTags: [],
    tags: [],
    // showLegend was removed
    keepDrawerOpen: true,
    useDailyTasks: false,
    showClocks: true,
    clockTimeFormat: "12h",
    clockFontTheme: "default",
  });
  const hasLoadedRef = useRef(false);
  const settingsRef = useRef(settings);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    const storedSettings = getItem("settings", null);
    if (storedSettings) {
      setSettings((prev) => {
        const newStoredSettings = { ...storedSettings };
        if (newStoredSettings.hasOwnProperty("showLegend")) {
          delete newStoredSettings.showLegend;
        }

        return {
          ...prev, // Start with new defaults (like useNotes: true)
          ...newStoredSettings, // Override with any stored settings
          // Ensure crucial defaults if they somehow got corrupted or are missing from older stored data
          // Explicitly check for stored value, otherwise use new default
          useNotes:
            newStoredSettings.useNotes === undefined
              ? true // New default for useNotes
              : newStoredSettings.useNotes,
          useTaskPriority:
            newStoredSettings.useTaskPriority === undefined
              ? prev.useTaskPriority
              : newStoredSettings.useTaskPriority,
          saveOnBrowserClose:
            newStoredSettings.saveOnBrowserClose === undefined
              ? true
              : newStoredSettings.saveOnBrowserClose,
          showClocks:
            newStoredSettings.showClocks === undefined
              ? true
              : newStoredSettings.showClocks,
          clockTimeFormat: newStoredSettings.clockTimeFormat || "12h",
          clockFontTheme: newStoredSettings.clockFontTheme || "default",
          tags: Array.isArray(newStoredSettings.tags)
            ? newStoredSettings.tags
            : [],
          selectedFilterTags: Array.isArray(
            newStoredSettings.selectedFilterTags
          )
            ? newStoredSettings.selectedFilterTags
            : [],
          defaultTaskDuration: Number.isFinite(
            newStoredSettings.defaultTaskDuration
          )
            ? newStoredSettings.defaultTaskDuration
            : DEFAULT_TASK_DURATION || 1800,
          keepDrawerOpen:
            newStoredSettings.keepDrawerOpen === undefined
              ? true
              : newStoredSettings.keepDrawerOpen,
        };
      });
    }
    hasLoadedRef.current = true;
  }, [getItem]);

  useEffect(() => {
    if (!hasLoadedRef.current || !settings) return;
    const handler = setTimeout(() => {
      setItem("settings", settings);
    }, 300);
    return () => {
      clearTimeout(handler);
    };
  }, [settings, setItem]);

  const toggleSetting = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const deleteTag = (tagToDelete) => {
    setSettings((prev) => {
      const newTags = prev.tags.filter((tag) => tag !== tagToDelete);
      const newSelectedFilterTags = prev.selectedFilterTags.filter(
        (tag) => tag !== tagToDelete
      );
      return {
        ...prev,
        tags: newTags,
        selectedFilterTags: newSelectedFilterTags,
      };
    });
  };

  const saveSettingsNow = () => {
    if (process.env.NODE_ENV === "development") {
      console.log(
        "[useSettings] Forcing save settings to storage now:",
        settingsRef.current
      );
    }
    setItem("settings", settingsRef.current);
  };

  const saveAllToFile = (onSaveCompleteCallback) => {
    try {
      const dataToSave = {
        tasks: JSON.parse(localStorage.getItem("tasks") || "[]"),
        todoLists: JSON.parse(localStorage.getItem("todoLists") || "[]"),
        dailyTasks: JSON.parse(localStorage.getItem("dailyTasks") || "[]"),
        settings: settingsRef.current,
      };
      const dataString = JSON.stringify(dataToSave);

      const blob = new Blob([dataString], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `multido_backup_${new Date().toISOString()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      localStorage.setItem(LAST_SAVED_SNAPSHOT_KEY, dataString);
      if (typeof onSaveCompleteCallback === "function") {
        onSaveCompleteCallback();
      }

      if (process.env.NODE_ENV === "development") {
        console.log("Saved data to file and updated snapshot:", dataToSave);
      }
    } catch (error) {
      console.error("Error saving data to file:", error);
    }
  };

  const clearLastSavedSnapshot = () => {
    localStorage.removeItem(LAST_SAVED_SNAPSHOT_KEY);
    if (process.env.NODE_ENV === "development") {
      console.log("[useSettings] Cleared last saved data snapshot.");
    }
  };

  const importData = (data, onImportCompleteCallback) => {
    try {
      const parsedData = data;
      const currentSettingsSnapshot = settingsRef.current;
      const importedSettingsData = parsedData.settings || {};

      if (importedSettingsData.hasOwnProperty("showLegend")) {
        delete importedSettingsData.showLegend;
      }

      const newSettings = {
        ...currentSettingsSnapshot,
        ...importedSettingsData,
        // Ensure imported data respects this, or new default if undefined
        useNotes:
          importedSettingsData.useNotes === undefined
            ? true // New default for useNotes
            : importedSettingsData.useNotes,
        useTaskPriority:
          importedSettingsData.useTaskPriority === undefined
            ? currentSettingsSnapshot.useTaskPriority
            : importedSettingsData.useTaskPriority,
        saveOnBrowserClose:
          importedSettingsData.saveOnBrowserClose === undefined
            ? true
            : importedSettingsData.saveOnBrowserClose,
        showClocks:
          importedSettingsData.showClocks === undefined
            ? true
            : importedSettingsData.showClocks,
        clockTimeFormat:
          importedSettingsData.clockTimeFormat ||
          currentSettingsSnapshot.clockTimeFormat ||
          "12h",
        clockFontTheme:
          importedSettingsData.clockFontTheme ||
          currentSettingsSnapshot.clockFontTheme ||
          "default",
        tags: Array.isArray(importedSettingsData.tags)
          ? importedSettingsData.tags
          : currentSettingsSnapshot.tags,
        selectedFilterTags: Array.isArray(
          importedSettingsData.selectedFilterTags
        )
          ? importedSettingsData.selectedFilterTags
          : currentSettingsSnapshot.selectedFilterTags,
        defaultTaskDuration: Number.isFinite(
          importedSettingsData.defaultTaskDuration
        )
          ? importedSettingsData.defaultTaskDuration
          : currentSettingsSnapshot.defaultTaskDuration,
        useDailyTasks:
          importedSettingsData.useDailyTasks === undefined
            ? currentSettingsSnapshot.useDailyTasks
            : importedSettingsData.useDailyTasks,
        keepDrawerOpen:
          importedSettingsData.keepDrawerOpen === undefined
            ? true
            : importedSettingsData.keepDrawerOpen,
      };
      setSettings(newSettings);

      clearLastSavedSnapshot();
      if (typeof onImportCompleteCallback === "function") {
        onImportCompleteCallback();
      }

      return {
        tasks: Array.isArray(parsedData.tasks) ? parsedData.tasks : [],
        todoLists: Array.isArray(parsedData.todoLists)
          ? parsedData.todoLists
          : [],
        dailyTasks: Array.isArray(parsedData.dailyTasks)
          ? parsedData.dailyTasks
          : [],
      };
    } catch (error) {
      console.error("Error importing settings:", error);
      return { tasks: [], todoLists: [], dailyTasks: [] };
    }
  };

  return {
    settings,
    toggleSetting,
    updateSetting,
    deleteTag,
    saveAllToFile,
    saveSettingsNow,
    importData,
    clearLastSavedSnapshot,
  };
}
